import { Timeline } from '../models/Timeline';
import { Track } from '../models/Track';
import { Element } from '../models/Element';

/**
 * TimelineSnapshot captures the state of a timeline for undo/redo operations
 * without reimplementing the Timeline class. It leverages the existing
 * getState/restoreState methods on Timeline elements.
 */
export class TimelineSnapshot {
  /**
   * Map of track IDs to element state snapshots
   */
  private trackElements: Map<
    string,
    Array<{ id: string; state: Record<string, any> }>
  > = new Map();

  /**
   * ID of the timeline captured
   */
  private timelineId: string;

  /**
   * Create a snapshot of the current timeline state
   * @param timeline The timeline to snapshot
   */
  constructor(timeline: Timeline) {
    this.timelineId = timeline.id;

    // Capture the state of all elements in all tracks
    timeline.tracks.forEach((track) => {
      // Store elements by their track ID
      const elements = track.elements.map((element) => ({
        id: element.id,
        state: element.getState(),
      }));

      // Only store tracks that have elements
      if (elements.length > 0) {
        this.trackElements.set(track.id, elements);
      }
    });
  }

  /**
   * Apply the snapshot state to a timeline
   * @param timeline The timeline to restore the state to
   * @returns True if restoration was successful
   */
  apply(timeline: Timeline): boolean {
    // Make sure we're applying to the same timeline
    if (timeline.id !== this.timelineId) {
      console.error('Cannot apply snapshot to different timeline');
      return false;
    }

    // First restore all elements in existing tracks
    this.trackElements.forEach((elements, trackId) => {
      const track = timeline.getTrack(trackId);

      if (!track) {
        console.warn(`Track ${trackId} not found when restoring snapshot`);
        return;
      }

      // Restore each element in this track
      elements.forEach(({ id, state }) => {
        const element = track.elements.find((el) => el.id === id);

        if (element) {
          // Restore element state
          element.restoreState(state);
        } else {
          // Element was deleted - recreate it
          const newElement = new Element({ id, type: state.type || 'video' });
          newElement.restoreState(state);
          timeline.addElement(trackId, newElement);
        }
      });
    });

    // Remove added tracks and elements
    timeline.tracks.forEach((track) => {
      if (!this.trackElements.has(track.id)) {
        timeline.removeTrack(track.id);
      } else {
        track.elements.forEach((element) => {
          if (
            !this.trackElements.get(track.id)?.some((e) => e.id === element.id)
          ) {
            timeline.removeElement(element.id);
          }
        });
      }
    });

    // Recalculate timeline duration to ensure it's consistent with the elements
    timeline.recalculateDuration();

    return true;
  }
}
